import { PaletteMode, Theme } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { lightPalette, darkPalette, typography, spacing, elevation } from '@my-helpers/common';

// Extend MUI theme for custom properties
declare module '@mui/material/styles' {
  interface Palette {
    border?: {
      default?: string;
      paper?: string;
      hover?: string;
    };
    alert?: {
      soft?: string;
      main?: string;
    };
  }
  interface PaletteOptions {
    border?: {
      default?: string;
      paper?: string;
      hover?: string;
    };
    alert?: {
      soft?: string;
      main?: string;
    };
  }
}

export const lightTheme: Theme = createTheme({
  palette: {
    mode: 'light',
    ...lightPalette,
  },
  typography: {
    fontFamily: typography.fontFamily.primary,
    h1: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h1 },
    h2: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h2 },
    h3: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h3 },
    h4: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h4 },
    h5: { fontWeight: typography.fontWeight.medium, fontSize: typography.fontSize.h5 },
    h6: { fontWeight: typography.fontWeight.medium, fontSize: typography.fontSize.h6 },
    button: {
      textTransform: 'none',
      fontWeight: typography.fontWeight.semibold,
    },
  },
  shape: {
    borderRadius: spacing.borderRadius.small,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: spacing.borderRadius.medium,
          padding: '10px 20px',
          fontWeight: typography.fontWeight.semibold,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: spacing.borderRadius.medium,
          boxShadow: elevation.light.level2,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: spacing.borderRadius.medium,
          },
        },
      },
    },
  },
});

export const darkTheme: Theme = createTheme({
  palette: {
    mode: 'dark',
    ...darkPalette,
  },
  typography: {
    fontFamily: typography.fontFamily.primary,
    h1: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h1 },
    h2: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h2 },
    h3: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h3 },
    h4: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h4 },
    h5: { fontWeight: typography.fontWeight.medium, fontSize: typography.fontSize.h5 },
    h6: { fontWeight: typography.fontWeight.medium, fontSize: typography.fontSize.h6 },
    button: {
      textTransform: 'none',
      fontWeight: typography.fontWeight.semibold,
    },
  },
  shape: {
    borderRadius: spacing.borderRadius.small,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: spacing.borderRadius.medium,
          padding: '10px 20px',
          fontWeight: typography.fontWeight.semibold,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: spacing.borderRadius.medium,
          boxShadow: elevation.dark.level2,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: spacing.borderRadius.medium,
          },
        },
      },
    },
  },
});

// Backwards compatible factory function
export const themeOptions = (mode: PaletteMode): Theme => {
  return mode === 'light' ? lightTheme : darkTheme;
};

export default themeOptions;